import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { BulletPoint } from "@lending/components/BulletPoint/BulletPoint";
import { HeroSection } from "@lending/components/HeroSection/HeroSection";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useCreateEligibilitySelectionForApplication } from "@lending/hooks/queries/EligibilityCriteria/useCreateEligibilitySelectionForApplication";
import { useGetEligibilityCriteriaQuery } from "@lending/hooks/queries/EligibilityCriteria/useGetEligibilityCriteriaQuery";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { EligibilityCriteriaCard } from "./EligibilityCriteriaCard";
import styles from "./EligibilitySelection.module.css";
import { useStore } from "../../../contexts/StoreContext";
import { useHeroBreakpoints } from "../../../hooks/useHeroBreakpoints";
import { usePageTransition } from "../../../hooks/usePageTransition";

interface SelectionState {
  criteriaId: string;
  optionId: string | null;
  freeformAnswer: string;
}

function EligibilitySelection() {
  const { m } = useHeroBreakpoints();
  const { l10n } = useLocalization();
  const { goNext } = useFlowNavigation();
  const { applicationUuid: routeApplicationUuid } = useParams<{ applicationUuid: string }>();
  const applicationUuid = useCurrentApplicationUuid();
  const { transitionClass } = usePageTransition();
  const { showGenericErrorToast } = useToastsContext();
  const { updateData } = useStore();
  const { createEligibilitySelectionForApplication } =
    useCreateEligibilitySelectionForApplication();
  const { eligibilityCriteria, hasEligibilityCriteria } = useGetEligibilityCriteriaQuery();
  const { data: application } = useGetPersonalLoanApplicationQuery({
    applicationUuid: routeApplicationUuid,
    enabled: Boolean(routeApplicationUuid),
  });
  const [selection, setSelection] = useState<SelectionState | null>(null);

  useEffect(() => {
    updateData({ hasEligibilityCriteria });
  }, [hasEligibilityCriteria, updateData]);

  useEffect(() => {
    if (selection || !application?.eligibility_selection) {
      return;
    }

    setSelection({
      criteriaId: application.eligibility_selection.criteria.id,
      optionId: application.eligibility_selection.selected_option?.id ?? null,
      freeformAnswer: application.eligibility_selection.freeform_answer ?? "",
    });
  }, [application?.eligibility_selection, selection]);

  const handleCriteriaSelect = (criteriaId: string) => {
    setSelection({ criteriaId, optionId: null, freeformAnswer: "" });
  };

  const handleOptionSelect = (criteriaId: string, optionId: string | null) => {
    setSelection({ criteriaId, optionId, freeformAnswer: "" });
  };

  const handleFreeformAnswerChange = (criteriaId: string, freeformAnswer: string) => {
    setSelection({ criteriaId, optionId: null, freeformAnswer });
  };

  const isSelectionComplete = (): boolean => {
    if (!selection) return false;

    const selectedCriteria = eligibilityCriteria.find((c) => c.id === selection.criteriaId);
    if (!selectedCriteria) return false;

    // If criteria has options, an option must be selected
    if (selectedCriteria.options.length > 0) {
      return selection.optionId !== null;
    }

    if (selectedCriteria.has_freeform_follow_up) {
      return selection.freeformAnswer.trim().length > 0;
    }

    // If criteria has no options, just selecting it is enough
    return true;
  };

  const handleContinue = async () => {
    if (!selection) return;

    const eligibilitySelection = {
      criteriaId: selection.criteriaId,
      optionId: selection.optionId,
      freeformAnswer: selection.freeformAnswer.trim(),
    };
    updateData({ eligibilitySelection });

    // Before the application exists, creating it posts the stashed selection.
    if (applicationUuid) {
      try {
        await createEligibilitySelectionForApplication(applicationUuid, eligibilitySelection);
      } catch {
        showGenericErrorToast();
        return;
      }
    }

    goNext();
  };

  return (
    <PageLayout>
      <div className={styles.container}>
        <HeroSection
          title={l10n.getString(
            "eligibility-selection-title",
            null,
            "Check your offer in less than 2 minutes"
          )}
          content={
            m && (
              <div className={styles.bulletPoints}>
                <BulletPoint
                  text={l10n.getString(
                    "eligibility-selection-bullet-1",
                    null,
                    "No impact to your credit score"
                  )}
                />
                <BulletPoint
                  text={l10n.getString(
                    "eligibility-selection-bullet-2",
                    null,
                    "No application fees"
                  )}
                />
                <BulletPoint
                  text={l10n.getString(
                    "eligibility-selection-bullet-3",
                    null,
                    "Get your offer instantly"
                  )}
                />
              </div>
            )
          }
        />
        <div className={`${styles.pageContent} ${transitionClass}`}>
          <div className={styles.heading}>
            {l10n.getString(
              "eligibility-selection-heading",
              null,
              "Let's make sure you're eligible"
            )}
          </div>

          <div className={styles.criteriaContainer}>
            {eligibilityCriteria.map((criteria, index) => (
              <EligibilityCriteriaCard
                key={criteria.id}
                index={index}
                label={criteria.label}
                followUpPlaceholder={criteria.follow_up_placeholder}
                options={criteria.options}
                hasFreeformFollowUp={criteria.has_freeform_follow_up}
                isSelected={selection?.criteriaId === criteria.id}
                selectedOptionId={selection?.criteriaId === criteria.id ? selection.optionId : null}
                freeformAnswer={
                  selection?.criteriaId === criteria.id ? selection.freeformAnswer : ""
                }
                onSelect={() => handleCriteriaSelect(criteria.id)}
                onOptionSelect={(optionId) => handleOptionSelect(criteria.id, optionId)}
                onFreeformAnswerChange={(value) => handleFreeformAnswerChange(criteria.id, value)}
              />
            ))}

            <div className={styles.continueButton}>
              {isSelectionComplete() && (
                <Button
                  label={l10n.getString("button-label-next", null, "Next")}
                  onClick={() => void handleContinue()}
                />
              )}
            </div>
          </div>

          {!m && (
            <div className={styles.mobilePromotionalDetailsCard}>
              <ul className="list--reset fontSize--s">
                <li className="margin--bottom--xs">
                  <i className="narmi-icon-check fontColor--theme--primary" />
                  <span className="margin--left--xxs fontWeight--semibold">
                    {l10n.getString(
                      "eligibility-selection-bullet-1",
                      null,
                      "No impact to your credit score"
                    )}
                  </span>
                </li>
                <li className="margin--bottom--xs">
                  <i className="narmi-icon-check fontColor--theme--primary" />
                  <span className="margin--left--xxs fontWeight--semibold">
                    {l10n.getString("eligibility-selection-bullet-2", null, "No application fees")}
                  </span>
                </li>
                <li>
                  <i className="narmi-icon-check fontColor--theme--primary" />
                  <span className="margin--left--xxs fontWeight--semibold">
                    {l10n.getString(
                      "eligibility-selection-bullet-3",
                      null,
                      "Get your offer instantly"
                    )}
                  </span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export { EligibilitySelection };
